import type {
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import {
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from "nuqs";

import useDebounce from "Infrastructure/Hooks/useDebounce";

interface TableSearchParamsDefaults {
  search?: string;
  pageIndex?: number;
  pageSize?: number;
  validSortIds?: [string, ...string[]];
  defaultSort?: { id: string; desc: boolean };
}

export function useTableSearchParams(defaults?: TableSearchParamsDefaults) {
  const [search, setSearch] = useQueryState(
    "search",
    parseAsString.withDefault(defaults?.search ?? ""),
  );
  const debouncedSearch = useDebounce(search, 400);

  const [pageIndex, setPageIndex] = useQueryState(
    "page",
    parseAsInteger.withDefault(defaults?.pageIndex ?? 0),
  );
  const [pageSize, setPageSize] = useQueryState(
    "size",
    parseAsInteger.withDefault(defaults?.pageSize ?? 10),
  );

  const sortParser = defaults?.validSortIds
    ? parseAsStringEnum(defaults.validSortIds)
    : parseAsString;

  const defaultAsc =
    defaults?.defaultSort && !defaults.defaultSort.desc
      ? defaults.defaultSort.id
      : "";
  const defaultDesc = defaults?.defaultSort?.desc
    ? defaults.defaultSort.id
    : "";

  const [sortAsc, setSortAsc] = useQueryState(
    "sortAsc",
    sortParser.withDefault(defaultAsc),
  );
  const [sortDesc, setSortDesc] = useQueryState(
    "sortDesc",
    sortParser.withDefault(defaultDesc),
  );

  const sorting: SortingState = sortAsc
    ? [{ id: sortAsc, desc: false }]
    : sortDesc
      ? [{ id: sortDesc, desc: true }]
      : [];

  const onSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const first = next[0];
    if (!first) {
      setSortAsc(null);
      setSortDesc(null);
    } else if (first.desc) {
      setSortAsc(null);
      setSortDesc(first.id);
    } else {
      setSortAsc(first.id);
      setSortDesc(null);
    }
  };

  const onPaginationChange = (updater: Updater<PaginationState>) => {
    const next =
      typeof updater === "function"
        ? updater({ pageIndex, pageSize })
        : updater;
    setPageIndex(next.pageIndex);
    setPageSize(next.pageSize);
  };

  const resetPage = () => setPageIndex(0);

  const onSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };

  return {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  };
}
